import { ReactComponent as IconFilterApplied } from '@/assets/svgs/adjustments-filter-applied.svg';
import { ReactComponent as IconFilter } from '@/assets/svgs/adjustments-filter.svg';
import Button from '@/components/Button';
import Dropdown from '@/components/Dropdown';
import rem from '@/utils/rem.js';
import classname from "classnames";
import { useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import KeyboardEventHandler from '@infinium/react-keyboard-event-handler';
import { usePopper } from "react-popper";
import PropTypes from 'prop-types';

const FilterDropDown = ({
	setFromDate,
	setEndDate,
	statusFilterList,
	setStatus,
	participantsFilterList,
	setParticipant,
	dataElementsFilterList,
	setDataElement,
	creatorFilterList,
	setCreator,
	handleClearFilter
}) => {
	const referenceRef = useRef(null);
	const popperRef = useRef(null);

	const [visible, setVisible] = useState(false);
	const [selectedDataElement, setSelectedDataElement] = useState(null)
	const [selectedParticipant, setSelectedParticipant] = useState(null)
	const [selectedPackageCreator, setSelectedPackageCreator] = useState(null)
	const [selectedStatus, setSelectedStatus] = useState(null)
	const [selectedFromDate, setSelectedFromDate] = useState("")
	const [selectedEndDate, setSelectedEndDate] = useState("")
	const [isFilterApplied, setIsFilterApplied] = useState(false)
	const [filterCount, setFilterCount] = useState(0)


	const { styles, attributes } = usePopper(
		referenceRef.current,
		popperRef.current,
		{
			placement: "bottom-end",
			modifiers: [
				{
					name: "offset",
					enabled: true,
					options: {
						offset: [0, 10],
					},
				},
			],
		}
	);

	useEffect(() => {
		document.addEventListener("mousedown", handleDocumentClick);
		return () => {
			document.removeEventListener("mousedown", handleDocumentClick);
		};
	}, []);

	const dropdownClass = classname({
		dropdown__content: true,
		"dropdown__content--active": visible,
		"cone-filter-dropdown__content": true,
	});

	function handleDropdownClick() {
		setVisible(!visible);
	}

	function handleDocumentClick(event) {
		if (
			referenceRef.current.contains(event.target) ||
			popperRef.current.contains(event.target)
		) {
			return;
		}
		setVisible(false);
	}

	function applyFilter() {
		setDataElement(selectedDataElement)
		setParticipant(selectedParticipant)
		setCreator(selectedPackageCreator)
		setStatus(selectedStatus)
		setFromDate(selectedFromDate)
		setEndDate(selectedEndDate)
		setVisible(false)

		numberOfFilterApplied()
	}

	function resetFilter() {
		setSelectedDataElement(null)
		setSelectedParticipant(null)
		setSelectedPackageCreator(null)
		setSelectedStatus(null)
		setSelectedFromDate("")
		setSelectedEndDate("")
		setIsFilterApplied(false)
		setFilterCount(0)
		setVisible(false)

		handleClearFilter()
	}

	function numberOfFilterApplied() {
		const filterList = [selectedDataElement, selectedParticipant, selectedPackageCreator, selectedStatus, selectedEndDate]
		const noOfAppliedFilter = filterList.filter(Boolean).length
		noOfAppliedFilter > 0 ? setIsFilterApplied(true) : setIsFilterApplied(false)
		setFilterCount(noOfAppliedFilter)
	}

	return (
		<div className="cone-filter" style={{ zIndex: 1 }}>
			<KeyboardEventHandler
				isExclusive={visible}
				handleKeys={["esc"]}
				onKeyEvent={(key, e) => {
					setVisible(false);
				}}
			/>

			<div className={"tooltip"}>
				<div
					data-testid="filter-dropdown"
					className={visible ? "package-landing__filter-icon--active" : "package-landing__filter-icon"}
					ref={referenceRef}
					onClick={handleDropdownClick}
					onKeyDown={handleDropdownClick}
				>
					{isFilterApplied ? <IconFilterApplied /> : <IconFilter />}
					<span className="tooltiptext__package-landing">
						{!isFilterApplied && 'Show search options'}
						{isFilterApplied && (
							`${filterCount} ${filterCount > 1 ? 'filters applied' : 'filter applied'}`
						)}

					</span>
				</div>
			</div>

			<div ref={popperRef} style={styles.popper} {...attributes.popper}>
				<div className={dropdownClass}>
					<div className="input">
						<div className="label">Data Element</div>
						<div className="group">
							<Dropdown
								placeholder="Search by Data Element"
								value={selectedDataElement?.elementName}
								filterVisible
								style={{ width: rem(320) }}
							>
								{dataElementsFilterList?.map((option) => {
									return (
										<Dropdown.Item key={option.dataElementId}
											id={option.elementName}
											onClick={() => { setSelectedDataElement(option) }}
										>
											{option.elementName}
										</Dropdown.Item>
									);
								})}
							</Dropdown>
						</div>
					</div>

					<div className="input">
						<div className="label">Package Participant</div>
						<div className="group">
							<Dropdown
								placeholder={"Search by Participant"}
								value={selectedParticipant?.name}
								filterVisible
								style={{ width: rem(320) }}
							>
								{participantsFilterList?.map((option) => {
									return (
										<Dropdown.Item key={option.orgId}
											id={option.orgId}
											onClick={() => { setSelectedParticipant(option) }}
										>
											{option.name}
										</Dropdown.Item>
									);
								})}
							</Dropdown>
						</div>
					</div>

					<div className="input">
						<div className="label">Package Creator</div>
						<div className="group">
							<Dropdown
								placeholder="Search by Package Creator"
								value={selectedPackageCreator?.name}
								style={{ width: rem(320) }}
							>
								{creatorFilterList.map((option) => {
									return (
										<Dropdown.Item key={option.orgId}
											id={option.orgId}
											onClick={() => setSelectedPackageCreator(option)}
										>
											{option.name}
										</Dropdown.Item>
									);
								})}
							</Dropdown>
						</div>
					</div>

					<div className="input">
						<div className="label">Package Status</div>
						<div className="group">
							<Dropdown
								placeholder="Search by Package Status"
								value={selectedStatus?.label}
								style={{ width: rem(320) }}
							>
								{statusFilterList?.map((option) => {
									return (
										<Dropdown.Item key={option.id}
											id={option.label}
											onClick={() => setSelectedStatus(option)}
										>
											{option.label}
										</Dropdown.Item>
									);
								})}
							</Dropdown>
						</div>
					</div>

					<div className="input">
						<div className="label">Date Period</div>
						<div className="group">
							<ReactDatePicker
								dateFormat="d MMM yyyy"
								value={selectedFromDate}
								selected={
									(selectedFromDate && new Date(selectedFromDate)) || null
								}
								todayButton="Today"
								dropdownMode="select"
								isClearable
								shouldCloseOnSelect
								showMonthDropdown
								showYearDropdown
								placeholderText="Start Date"
								onChange={(val) => {
									setSelectedFromDate(val);
								}}
							/>
							<span>-</span>
							<ReactDatePicker
								dateFormat="d MMM yyyy"
								value={selectedEndDate}
								selected={
									(selectedEndDate && new Date(selectedEndDate)) || null
								}
								todayButton="Today"
								dropdownMode="select"
								isClearable
								shouldCloseOnSelect
								showMonthDropdown
								showYearDropdown
								placeholderText="End Date"
								onChange={(val) => {
									val.setHours(23, 59, 59, 999);
									setSelectedEndDate(val);
								}}
							/>
						</div>
					</div>

					<div className="filter-option__action-btn">
						<div className="filter-option__button">
							<Button ghost text="Reset"
								submit={resetFilter}
							/>
						</div>
						<Button full text="Apply"
							maxWidth={98}
							submit={applyFilter}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

FilterDropDown.propTypes = {
	setFromDate: PropTypes.func,
	setEndDate: PropTypes.func,
	statusFilterList: PropTypes.array,
	setStatus: PropTypes.func,
	participantsFilterList: PropTypes.array,
	setParticipant: PropTypes.func,
	dataElementsFilterList: PropTypes.array,
	setDataElement: PropTypes.func,
	creatorFilterList: PropTypes.array,
	setCreator: PropTypes.func,
	handleClearFilter: PropTypes.func
}

export default FilterDropDown;
